import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS } from "@/data/content";
import { useAnchorNav } from "@/lib/nav";
import { Logo } from "@/components/Logo";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const go = useAnchorNav();
  const solid = scrolled || pathname !== "/" || open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleNav = (e, link) => {
    setOpen(false);
    go(e, link.id);
  };

  const linkHref = (l) => (pathname === "/" ? l.href : `/${l.href}`);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
          solid ? "border-b border-white/10 bg-navy/95 backdrop-blur-md" : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" aria-label="Tillvia – till startsidan" data-testid="header-logo" onClick={() => setOpen(false)}>
            <Logo />
          </Link>
          <nav aria-label="Huvudmeny" className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={linkHref(l)}
                data-testid={l.testId}
                onClick={(e) => handleNav(e, l)}
                className="font-display text-sm font-semibold text-cream/80 transition-colors hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={pathname === "/" ? "#kontakt" : "/#kontakt"}
              data-testid="nav-cta-button"
              onClick={(e) => handleNav(e, { id: "kontakt" })}
              className="hidden items-center gap-2 rounded-lg bg-orange px-5 py-2.5 font-display text-sm font-bold text-navy shadow-[0_4px_14px_rgba(255,125,0,0.25)] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-orange2 sm:inline-flex"
            >
              Boka möte <ArrowRight size={16} aria-hidden="true" />
            </a>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? "Stäng menyn" : "Öppna menyn"}
              onClick={() => setOpen(!open)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-cream transition-colors hover:bg-white/10 lg:hidden"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 top-[72px] z-40 bg-navy lg:hidden"
          >
            <nav aria-label="Mobilmeny" className="flex h-full flex-col overflow-y-auto px-6 pb-10 pt-4">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={linkHref(l)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.3 }}
                  onClick={(e) => handleNav(e, l)}
                  data-testid={`mobile-${l.testId}`}
                  className="border-b border-white/10 py-4 font-display text-2xl font-bold text-cream transition-colors hover:text-orange"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href={pathname === "/" ? "#kontakt" : "/#kontakt"}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.3 }}
                onClick={(e) => handleNav(e, { id: "kontakt" })}
                data-testid="mobile-nav-cta-button"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-lg bg-orange px-6 py-4 font-display text-base font-bold text-navy"
              >
                Boka möte <ArrowRight size={18} aria-hidden="true" />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
